/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import ObjectTypeModel from 'cio/models/object-type';
import { tracked } from '@glimmer/tracking';
import AlertsService from 'cio/services/alerts';
import DS from 'ember-data';
import RouterService from '@ember/routing/router-service';
import AuthService from 'cio/services/auth';
import { isEmpty } from '@ember/utils';

interface ObjectTypeSetupComponentArgs {
  model: ObjectTypeModel;
}

enum Steps {
  CHOOSE = 'choose',
  CONFIGURE = 'configure',
}

interface TypeOption {
  name: string;
  singularName: string;
  slug: string;
  singularSlug: string;
  icon?: string;
  description?: string;
  disabled?: boolean;
  typeIcon?: string;
}
const TYPES: TypeOption[] = [
  {
    name: 'Companies',
    singularName: 'Company',
    slug: 'companies',
    singularSlug: 'company',
    icon: 'briefcase',
    typeIcon: 'company',
    description: 'Entities that people are employed by',
  },
  {
    name: 'Accounts',
    singularName: 'Account',
    slug: 'accounts',
    singularSlug: 'account',
    icon: 'plant-in-hand',
    typeIcon: 'account-box',
    description: 'Entities that you are targeting, selling, or supporting',
  },
  {
    name: 'Appointments',
    singularName: 'Appointment',
    slug: 'appointments',
    singularSlug: 'appointment',
    icon: 'feature-list',
    typeIcon: 'copy-to-clipboard',
    description: 'Scheduled engagements or meetings - like medical, professional services, etc.',
  },
  {
    name: 'Events',
    singularName: 'Event',
    slug: 'events',
    singularSlug: 'event',
    icon: 'calendar-events',
    typeIcon: 'calendar',
    description: 'Planned gatherings of people - like conferences, meetups, etc.',
  },
  {
    name: 'Courses',
    singularName: 'Course',
    slug: 'courses',
    singularSlug: 'course',
    icon: 'color-palette',
    typeIcon: 'integrations',
    description: 'Set of classes on a particular subject - like business, arts, etc.',
  },
];

const EMPTY_FORM: TypeOption = {
  name: '',
  singularName: '',
  slug: '',
  singularSlug: '',
};

export default class ObjectTypeSetupComponent extends Component<ObjectTypeSetupComponentArgs> {
  @service alerts!: AlertsService;
  @service store!: DS.Store;
  @service router!: RouterService;
  @service auth!: AuthService;

  @tracked step = Steps.CHOOSE;
  @tracked hidePredefinedStep = false;
  steps = Steps;

  constructor(owner: unknown, args: ObjectTypeSetupComponentArgs) {
    super(owner, args);

    // if some option is not disabled, then do nothing
    // else --> in reverse logic - go right away to Form step and hide disabled predefines
    if (!this.types.some((option) => option.disabled !== true)) {
      this.hidePredefinedStep = true;
      this.step = Steps.CONFIGURE;
    }
  }

  get objectTypes() {
    return this.store
      .peekAll('object-type')
      .toArray()
      .filter((objectType) => !objectType.get('isNew'));
  }

  get types() {
    let types = TYPES.map(({ ...type }) => {
      if (
        this.objectTypes.some(
          (objectType) =>
            objectType.name.toLowerCase() === type.name.toLowerCase() || objectType.slug.toLowerCase() === type.slug,
        )
      ) {
        type.disabled = true;
      }

      return type;
    });
    return types;
  }

  get isConfigureDisabled() {
    return isEmpty(this.args.model.name);
  }

  get nextText() {
    let { name } = this.args.model;
    if (!isEmpty(name)) {
      return `Next: Configure ${name}`;
    }
    return 'Next';
  }

  @action
  changeStep(value: Steps) {
    this.step = value;
  }

  @action
  onSelectType(option: TypeOption) {
    let { model } = this.args;
    model.setProperties({
      name: option.name,
      singularName: option.singularName,
      slug: option.slug,
      singularSlug: option.singularSlug,
      icon: option.typeIcon,
    });
  }

  @action
  createNewType() {
    this.onSelectType(EMPTY_FORM);
    this.changeStep(this.steps.CONFIGURE);
  }
}
